import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Dice5, Users, X } from "lucide-react";
import toast from "react-hot-toast";
import { useLang } from "../context/LangContext";
import { friendDuelAPI } from "../api";

const CustomTestPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [count, setCount] = useState(50);
  const counts = [50, 100, 200, 400];

  // ─── Friend invite modal state ─────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [friendCode, setFriendCode] = useState("");
  const [inviting, setInviting] = useState(false);

  const handleInvite = async () => {
    const code = friendCode.trim();
    if (code.length === 0) {
      toast.error("Kod kiriting");
      return;
    }

    setInviting(true);
    try {
      const res = await friendDuelAPI.invite({
        uniqueCode: code,
        questionCount: count,
      });
      const data = res.data;
      setShowModal(false);
      setFriendCode("");
      navigate("/friend-duel", {
        state: {
          role: "inviter",
          inviteId: data.inviteId,
          friendName: data.friendName || data.receiverName || "Do'stingiz",
          friendPhoto: data.friendPhoto || data.receiverPhoto || "",
          questionCount: count,
          expiresAt: data.expiresAt,
        },
      });
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || "";

      if (status === 404) {
        toast.error("Bu kod bilan foydalanuvchi topilmadi");
      } else if (msg.toLowerCase().includes("o'zingizga") || msg.toLowerCase().includes("yourself")) {
        toast.error("O'z kodingizni kira olmaysiz");
      } else if (msg.toLowerCase().includes("allaqachon") || msg.toLowerCase().includes("already")) {
        toast.error("Bu do'stga taklif allaqachon yuborilgan");
      } else {
        toast.error(msg || "Xatolik yuz berdi");
      }
    } finally {
      setInviting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* Header */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: "calc(52px + env(safe-area-inset-top))",
          paddingTop: "env(safe-area-inset-top)",
        }}>
        <div
          style={{
            maxWidth: 520,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
            }}>
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            {t("randomTest")}
          </span>
        </div>
      </div>

      <div
        style={{ maxWidth: 520, margin: "0 auto", padding: "100px 16px 32px" }}>
        <div
          className="fade-up"
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 24,
            padding: "24px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}>
          {/* Title */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginBottom: 24,
            }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: "rgba(59,130,246,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Dice5 size={32} style={{ color: "#3B82F6" }} />
            </div>
            <div>
              <h2
                style={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 20,
                  margin: 0,
                }}>
                {t("randomTest")}
              </h2>
              <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 4 }}>
                {t("chooseAmountAndStart")}
              </p>
            </div>
          </div>

          {/* Count selector */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              marginBottom: 24,
            }}>
            {counts.map((n) => (
              <button
                key={n}
                onClick={() => setCount(n)}
                style={{
                  padding: "14px 0",
                  borderRadius: 14,
                  border: "2px solid",
                  borderColor:
                    count === n ? "#3B82F6" : "rgba(255,255,255,0.05)",
                  background:
                    count === n ? "#3B82F6" : "rgba(255,255,255,0.05)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 17,
                  cursor: "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow:
                    count === n ? "0 0 20px rgba(59,130,246,0.3)" : "none",
                  transform: count === n ? "scale(1.02)" : "scale(1)",
                }}>
                {n}
              </button>
            ))}
          </div>

          {/* Tasodifiy boshlash */}
          <button
            onClick={() => navigate(`/quiz/random/${count}`)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "16px",
              borderRadius: 16,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 800,
              fontSize: 15,
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 15px rgba(59,130,246,0.4)",
              marginBottom: 10,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#2563EB")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "#3B82F6")
            }>
            🎲 {t("startRandom")} <Play size={16} fill="currentColor" />
          </button>

          {/* Do'st bilan o'ynash — YANGI */}
          <button
            onClick={() => setShowModal(true)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "16px",
              borderRadius: 16,
              border: "1px solid rgba(139,92,246,.4)",
              background: "rgba(139,92,246,.1)",
              color: "#a78bfa",
              fontWeight: 800,
              fontSize: 15,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(139,92,246,.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(139,92,246,.1)";
            }}>
            <Users size={18} /> Do'st bilan o'ynash
          </button>
        </div>

        <p
          style={{
            textAlign: "center",
            color: "var(--muted)",
            fontSize: 13,
            marginTop: 20,
            lineHeight: 1.5,
            padding: "0 20px",
          }}>
          {t("randomInfo")}
        </p>
      </div>

      {/* ─── INVITE MODAL ─────────────────────────────────────────────────────── */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9000,
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}>
          <div
            style={{
              background: "#111827",
              border: "1px solid #1e2d45",
              borderRadius: 20,
              padding: "24px 20px",
              width: "100%",
              maxWidth: 360,
            }}>
            {/* Modal header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
              }}>
              <div>
                <h3
                  style={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 16,
                    margin: 0,
                  }}>
                  Do'stingiz kodini kiriting
                </h3>
                <p
                  style={{
                    color: "#6b7280",
                    fontSize: 12,
                    marginTop: 4,
                  }}>
                  {count} ta savol • ~{Math.ceil(count / 10)} daqiqa
                </p>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  setFriendCode("");
                }}
                style={{
                  background: "rgba(255,255,255,.05)",
                  border: "1px solid rgba(255,255,255,.1)",
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#9ca3af",
                }}>
                <X size={16} />
              </button>
            </div>

            {/* Code input */}
            <input
              type="text"
              value={friendCode}
              onChange={(e) => setFriendCode(e.target.value.replace(/\D/g, "").slice(0, 9))}
              placeholder="_ _ _ _ _ _ _ _ _"
              maxLength={9}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              style={{
                width: "100%",
                padding: "14px 16px",
                borderRadius: 12,
                border: "1px solid #1e2d45",
                background: "rgba(255,255,255,.04)",
                color: "#fff",
                fontSize: 20,
                fontWeight: 800,
                textAlign: "center",
                letterSpacing: 6,
                outline: "none",
                marginBottom: 20,
                boxSizing: "border-box",
                fontFamily: "monospace",
              }}
              autoFocus
            />

            {/* Actions */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setFriendCode("");
                }}
                style={{
                  flex: 1,
                  padding: "14px",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,.1)",
                  background: "transparent",
                  color: "#9ca3af",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                }}>
                Bekor
              </button>
              <button
                onClick={handleInvite}
                disabled={inviting || friendCode.length === 0}
                style={{
                  flex: 2,
                  padding: "14px",
                  borderRadius: 14,
                  border: "none",
                  background:
                    inviting || friendCode.length === 0 ?
                      "rgba(139,92,246,.3)"
                    : "linear-gradient(135deg, #8B5CF6, #6D28D9)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 14,
                  cursor:
                    inviting || friendCode.length === 0 ?
                      "not-allowed"
                    : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}>
                {inviting ?
                  <>
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,.3)",
                        borderTop: "2px solid #fff",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Yuborilmoqda...
                  </>
                : "Taklif yuborish →"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CustomTestPage;
